import React from 'react';
import { useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiMapPin, FiCheck } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { destinations, SITE_URL, AIRPORT_COORDS, PORT_COORDS } from '../config/seo-data';

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const buildBookingUrl = (pickup, dropoff) => {
  const params = new URLSearchParams();
  if (pickup) {
    params.set('pickup_name', pickup.name);
    params.set('pickup_lat', pickup.lat);
    params.set('pickup_lng', pickup.lng);
  }
  if (dropoff) {
    params.set('dropoff_name', dropoff.name);
    params.set('dropoff_lat', dropoff.lat);
    params.set('dropoff_lng', dropoff.lng);
  }
  return `/booking?${params.toString()}`;
};

const DestinationTransfer = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const dest = destinations.find((d) => d.slug === slug);

  if (!dest) {
    return <Navigate to="/" replace />;
  }

  const isFromPort = searchParams.get('from') === 'port';
  const originCoords = isFromPort ? PORT_COORDS : AIRPORT_COORDS;
  const originLabel = isFromPort ? 'Corfu Port' : 'Corfu Airport';
  const originShort = isFromPort ? 'Port' : 'Airport';
  const fromParam = isFromPort ? '?from=port' : '';

  const related = destinations
    .filter((d) => d.slug !== slug && d.area === dest.area)
    .slice(0, 3);

  const otherPopular = destinations
    .filter((d) => d.slug !== slug && d.popular)
    .slice(0, 4);

  return (
    <SEOPageLayout
      title={`${originLabel} to ${dest.name} Transfer | Private Chauffeur - Lasmari Chauffeurs`}
      description={`Book a private transfer from ${originLabel} to ${dest.name}. ${dest.duration} drive, ${dest.distance}. Mercedes-Benz, meet & greet${isFromPort ? ', port pickup' : ', flight monitoring'}.`}
      keywords={`${originLabel.toLowerCase()} to ${dest.name.toLowerCase()} transfer, ${dest.name.toLowerCase()} corfu transfer, taxi ${originLabel.toLowerCase()} to ${dest.name.toLowerCase()}`}
      canonical={`${SITE_URL}/transfers/${dest.slug}`}
    >
      {/* Hero */}
      <section className="relative py-24 lg:py-36 bg-gradient-to-br from-[#1a1012] via-[#8e1107] to-[#1a1012] overflow-hidden">
        {dest.image && (
          <div className="absolute inset-0">
            <img src={dest.image} alt={dest.name} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />
          </div>
        )}
        {/* Radial scrim pool behind centered text for local contrast */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-white/85 text-sm uppercase tracking-widest mb-4 font-light" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}>
            {dest.area} • {dest.distance} from {originShort}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl lg:text-6xl font-light text-white mb-6" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}>
            {originLabel} to {dest.name}
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="flex items-center justify-center gap-6 text-white/85 mb-8"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
          >
            <span className="flex items-center gap-2"><FiClock className="w-4 h-4 text-white" /> {dest.duration}</span>
            <span className="flex items-center gap-2"><FiMapPin className="w-4 h-4 text-white" /> {dest.distance}</span>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Link to={dest.coords ? buildBookingUrl(originCoords, { name: dest.name, ...dest.coords }) : '/booking'} className="inline-block bg-[#ba1609] text-white px-8 py-3 rounded-full font-medium hover:bg-[#8e1107] transition-colors text-lg">
              Book Transfer to {dest.name}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About + Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <motion.h2 {...fadeUp} className="text-3xl font-light text-[#1a1012] mb-6">
                About {dest.name}
              </motion.h2>
              <p className="text-[#5b4d4f] leading-relaxed mb-6">{dest.longDescription}</p>

              <h3 className="text-[#1a1012] font-medium mb-4">Top Things to See</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {dest.highlights.map((h) => (
                  <span key={h} className="bg-[#fbeceb] border border-black/10 text-[#ba1609] px-3 py-1.5 rounded-lg text-sm">
                    {h}
                  </span>
                ))}
              </div>

              <h3 className="text-[#1a1012] font-medium mb-4">How to Get From {originLabel} to {dest.name}</h3>
              <p className="text-[#5b4d4f] leading-relaxed mb-4">
                The best way is a <strong className="text-[#1a1012]">pre-booked private transfer</strong>, {dest.duration} in a comfortable Mercedes-Benz with air conditioning.
              </p>
              {isFromPort ? (
                <p className="text-[#5b4d4f] leading-relaxed">
                  Your driver will meet you at the cruise terminal or ferry port exit with a name sign. No waiting in rank queues, step off your ship and into a comfortable, air-conditioned Mercedes-Benz heading straight to {dest.name}.
                </p>
              ) : (
                <p className="text-[#5b4d4f] leading-relaxed">
                  Alternatives include the metered rank service (subject to availability, can be scarce June-September) or KTEL public buses (limited schedule, may require a change in Corfu Town). A private transfer is recommended for comfort, reliability, and guaranteed pickup.
                </p>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <div className="bg-[#faf7f6] border border-black/10 rounded-xl p-6 sticky top-24 shadow-sm">
                <h3 className="text-[#1a1012] font-medium text-lg mb-4">Transfer Details</h3>
                <div className="space-y-4">
                  {[
                    ['From', originLabel],
                    ['Distance', dest.distance],
                    ['Duration', dest.duration],
                    ['Vehicle', 'Mercedes-Benz (A/C)'],
                    ['Child Seats', 'Free on request'],
                    ['Cancellation', 'Free up to 24h before'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between items-center border-b border-black/10 pb-2">
                      <span className="text-[#5b4d4f] text-sm">{label}</span>
                      <span className="text-[#1a1012] text-sm font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                <h4 className="text-[#1a1012] text-sm font-medium mt-6 mb-3">Included:</h4>
                <ul className="space-y-2">
                  {[
                    isFromPort ? 'Meet at terminal exit' : 'Meet & greet at arrivals',
                    isFromPort ? 'Port pickup coordination' : 'Flight monitoring',
                    'Luggage assistance',
                    'Complimentary water',
                    'Door-to-door service'
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-[#5b4d4f] text-sm">
                      <FiCheck className="w-4 h-4 text-[#ba1609] shrink-0" /> {item}
                    </li>
                  ))}
                </ul>

                <Link to={dest.coords ? buildBookingUrl(originCoords, { name: dest.name, ...dest.coords }) : '/booking'} className="block w-full mt-6 bg-[#ba1609] text-white text-center py-3 rounded-full font-medium hover:bg-[#8e1107] transition-colors">
                  Book This Transfer
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Destinations */}
      {(related.length > 0 || otherPopular.length > 0) && (
        <section className="py-20 bg-[#faf7f6]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-light text-[#1a1012] mb-8">
              {related.length > 0 ? `Other Transfers in ${dest.area}` : 'Popular Transfers'}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(related.length > 0 ? related : otherPopular).map((d) => (
                <Link
                  key={d.slug}
                  to={`/transfers/${d.slug}${fromParam}`}
                  className="bg-white border border-black/10 rounded-lg p-4 shadow-sm hover:border-[#ba1609]/30 transition-all group"
                >
                  <h3 className="text-[#1a1012] font-medium group-hover:text-[#ba1609] transition-colors mb-1">{d.name}</h3>
                  <p className="text-[#5b4d4f] text-xs mb-2">{d.area}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-[#5b4d4f] text-xs">{d.duration}</span>
                    <span className="text-[#ba1609] text-sm font-medium">View →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SEOPageLayout>
  );
};

export default DestinationTransfer;
